import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../lib/config";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Loader2, LogIn, X } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";

export default function MyAccountLookup({ open, onClose }) {
  const nav = useNavigate();
  const [phone, setPhone] = useState("");
  const [pin, setPin] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e?.preventDefault();
    if (!phone.replace(/\D/g, "").length || pin.length !== 4) {
      toast.error("Llena tu teléfono y los 4 dígitos del PIN");
      return;
    }
    setLoading(true);
    try {
      const { data } = await api.post("/registrations/lookup", { phone, pin });
      const accs = data.accounts || [{ id: data.id, full_name: data.full_name }];
      // Save session so user doesn't have to log in again
      try {
        localStorage.setItem("rescate_session", JSON.stringify({
          phone, accounts: accs, saved_at: new Date().toISOString(),
        }));
      } catch {}
      if (accs.length > 1) {
        toast.success(`Encontramos ${accs.length} inscripciones bajo este teléfono`);
        onClose?.();
        nav("/login");
      } else {
        toast.success(`¡Bienvenido${accs[0].full_name ? ", " + accs[0].full_name.split(" ")[0] : ""}!`);
        onClose?.();
        nav(`/mi-cuenta/${accs[0].id}`);
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "No te pudimos encontrar");
    } finally {
      setLoading(false);
    }
  };

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-[100] bg-black/80 backdrop-blur-sm flex items-center justify-center px-6"
          onClick={onClose}
          data-testid="my-account-lookup-modal"
        >
          <motion.form
            onSubmit={submit}
            initial={{ scale: 0.9, y: 20 }}
            animate={{ scale: 1, y: 0 }}
            exit={{ scale: 0.9, y: 20 }}
            onClick={(e) => e.stopPropagation()}
            className="bg-white rounded-3xl p-8 max-w-md w-full shadow-2xl relative"
          >
            <button
              type="button"
              onClick={onClose}
              className="absolute top-4 right-4 w-8 h-8 rounded-full bg-gray-100 hover:bg-gray-200 flex items-center justify-center"
              data-testid="lookup-close"
            >
              <X className="w-4 h-4" />
            </button>

            <div className="flex justify-center mb-4">
              <div className="w-16 h-16 rounded-full bg-gradient-to-br from-yellow-400 to-orange-500 flex items-center justify-center">
                <LogIn className="w-8 h-8 text-black" />
              </div>
            </div>
            <h2 className="font-heading uppercase text-2xl text-center text-black mb-2">Mi cuenta</h2>
            <p className="text-sm text-gray-600 text-center mb-6">
              Accede a tu inscripción para ver tu progreso, pagar cuotas y editar tus datos.
            </p>

            <div className="space-y-4">
              <div>
                <Label htmlFor="lookup-phone">Tu teléfono</Label>
                <Input
                  id="lookup-phone"
                  data-testid="lookup-phone"
                  type="tel"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  placeholder="614-555-1234"
                  className="mt-1 h-12 text-lg !text-black bg-white placeholder:text-gray-400"
                  autoFocus
                />
              </div>
              <div>
                <Label htmlFor="lookup-pin">PIN (últimos 4 dígitos del teléfono)</Label>
                <Input
                  id="lookup-pin"
                  data-testid="lookup-pin"
                  type="tel"
                  maxLength={4}
                  inputMode="numeric"
                  pattern="[0-9]*"
                  value={pin}
                  onChange={(e) => setPin(e.target.value.replace(/\D/g, "").slice(0, 4))}
                  placeholder="••••"
                  className="mt-1 h-14 text-center text-3xl tracking-[0.8em] font-bold !text-black bg-white placeholder:text-gray-300"
                />
              </div>
            </div>

            <Button
              type="submit"
              disabled={loading}
              data-testid="lookup-submit"
              className="w-full mt-6 h-12 bg-gradient-to-r from-yellow-400 to-orange-500 hover:from-yellow-500 hover:to-orange-600 text-black font-bold uppercase tracking-wider"
            >
              {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : "Entrar a mi cuenta"}
            </Button>

            <p className="text-[11px] text-gray-500 text-center mt-4">
              ¿Aún no te has inscrito? <button type="button" onClick={() => { onClose?.(); document.getElementById("inscripcion")?.scrollIntoView({ behavior: "smooth" }); }} className="text-orange-600 font-semibold hover:underline">Regístrate aquí</button>
            </p>
          </motion.form>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
